import React, { useState } from "react";
import { EnvelopeOpenIcon, DevicePhoneMobileIcon, ArrowLeftIcon, ArrowUturnLeftIcon, ClockIcon, CheckIcon, ClipboardDocumentIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import Input from "src/components/form/Input";
import Button from "src/components/form/Button";

const TwoFactorView = ({ twoFactorDetails, email, otp, setOtp, verifyOtp, resendOtp, resendOtpSecond, disabledResendOtp, formatTime, site, lockout, otpSecond, disabled }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [activeMethod, setActiveMethod] = useState(twoFactorDetails?.two_factor_default_method || "email");

  const enabledMethods = twoFactorDetails?.two_factor_enabled_methods || ["email"];
  const hasMultipleMethods = enabledMethods.length > 1;

  const needsTotpSetup = activeMethod === "totp" && !twoFactorDetails?.two_factor_totp_initialized;

  const handleSwitchMethod = (method) => {
    setActiveMethod(method);
    setShowPicker(false);
    setOtp("");
    if (method === "email") resendOtp();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  if (showPicker) {
    return (
      <div className="flex w-full flex-col items-center space-y-4">
        <h3 className="text-lg font-bold text-gray-900">Select Verification Method</h3>
        <div className="animate-fade-in grid w-full gap-3 ">
          {enabledMethods.map((m) => (
            <button
              key={m}
              disabled={disabledResendOtp}
              onClick={() => handleSwitchMethod(m)}
              className={`flex w-full items-center justify-between rounded-md border-2 p-4 transition-all ${activeMethod === m ? "bg-highlightColor/5 border-highlightColor" : "border-gray-100 hover:border-gray-200"} ${disabledResendOtp ? "cursor-not-allowed opacity-50" : ""}`}>
              <div className="flex items-center gap-3">
                {m === "email" ? <EnvelopeOpenIcon className="h-6 w-6 text-highlightColor" /> : <DevicePhoneMobileIcon className="h-6 w-6 text-highlightColor" />}
                <div className="text-left">
                  <p className="text-sm font-bold text-gray-900">{m === "email" ? "Email Address" : "Authenticator App"}</p>
                  <p className="text-xs text-gray-500">{m === "email" ? email : "Authenticator App"}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowPicker(false)}
          className="flex items-center pt-3 text-xs font-medium text-gray-500 transition-colors hover:text-gray-800">
          <ArrowLeftIcon className="mr-1 h-3.5 w-3.5 stroke-[3px]" /> Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="animate-2fa-fade w-full">
      {needsTotpSetup ? (
        <div className="mb-6 space-y-4 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center">
          <div>
            <h4 className="text-sm font-bold text-gray-900">Set up Authenticator</h4>
            <p className="text-[11px] text-gray-500">Scan QR code or enter the key manually.</p>
          </div>

          {twoFactorDetails?.qrCode && (
            <div className="mx-auto block h-32 w-32 overflow-hidden rounded-lg border border-gray-100 bg-white p-2 shadow-sm">
              <img
                src={twoFactorDetails.qrCode}
                alt="QR Code"
                className="h-full w-full"
              />
            </div>
          )}

          <div className="flex flex-col items-center gap-2">
            <code className="rounded bg-gray-200/50 px-2 py-1 font-mono text-[10px] font-bold text-gray-600">{twoFactorDetails?.secretKey || "TOKEN_SECRET"}</code>
            <button
              type="button"
              onClick={() => copyToClipboard(twoFactorDetails?.secretKey)}
              className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-600 hover:text-slate-900">
              {isCopied ? (
                <>
                  <CheckIcon className="h-3 w-3" /> Copied
                </>
              ) : (
                <>
                  <ClipboardDocumentIcon className="h-3 w-3" /> Copy Key
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="mb-4 flex flex-col items-center justify-center">
          <p className="text-sm text-gray-700">
            {activeMethod === "email" ? (
              <>
                {disabledResendOtp === false && (
                  <p className="text-xs font-medium text-highlightColor">
                    <EnvelopeOpenIcon className="mr-2 inline h-4 w-4" />
                    Validation code sent to <span className="font-bold">{email}</span>
                  </p>
                )}
              </>
            ) : (
              <div className="rounded-lg bg-gray-50 p-3">
                <p className="text-center text-xs font-medium text-highlightColor">
                  <DevicePhoneMobileIcon className="mr-2 inline h-4 w-4" />
                  Enter the 6-digit code from your authenticator app.
                </p>
              </div>
            )}
          </p>
          {activeMethod === "email" && (
            <div className="mt-2 flex text-xs text-gray-700">
              {disabledResendOtp ? (
                <div className="flex items-center gap-2">
                  <ArrowPathIcon className="h-4 w-4 animate-spin text-highlightColor" />
                  <span className="text-xs font-medium text-gray-500">Sending code...</span>
                </div>
              ) : resendOtpSecond > 0 ? (
                <p>
                  Wait <ClockIcon className="mb-[2px] inline-flex h-3 w-3" /> <span className="font-semibold">{formatTime(resendOtpSecond)}</span>
                </p>
              ) : (
                <p>
                  No code?{" "}
                  <button
                    type="button"
                    onClick={resendOtp}
                    className="underline hover:text-highlightColor">
                    Resend
                  </button>
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mb-4">
        <Input
          inline
          inputValue={otp}
          onChange={(e) => {
            if (e.target.value && !/^[0-9]+$/.test(e.target.value)) return;
            if (e.target.value.length > 6) return;
            setOtp(e.target.value);
          }}
          label={activeMethod === "email" ? "Email OTP" : "Authenticator code"}
          inputType={"text"}
          inputName={"otp"}
          onKeyDown={(e) => e.key === "Enter" && verifyOtp(otp, activeMethod)}
        />
      </div>

      <Button
        isDisable={disabled || lockout || otpSecond > 0 || (activeMethod === "email" && otp.length < 4)}
        buttonClasses={"w-full"}
        loading={disabled}
        buttonType={"Submit"}
        buttonLabel={lockout ? "Locked" : otpSecond > 0 ? `Blocked: ${formatTime(otpSecond)}` : "Verify"}
        buttonFunction={(e) => verifyOtp(otp, activeMethod)}
        buttonStyles={{ borderColor: site?.highlight_color, backgroundColor: site?.highlight_color }}
      />

      <div className="mt-6 flex items-center justify-between px-1">
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="flex items-center gap-1 text-[11px] font-semibold text-gray-500 underline underline-offset-4 hover:text-gray-900">
          <ArrowUturnLeftIcon className="h-3 w-3" /> Back to Login
        </button>
        {hasMultipleMethods && (
          <button
            type="button"
            onClick={() => setShowPicker(true)}
            className="text-[11px] font-semibold text-gray-500 underline underline-offset-4 hover:text-gray-900">
            Try another way
          </button>
        )}
      </div>
    </div>
  );
};

export default TwoFactorView;
